"use client";
import * as React from "react";
import { Search, Bell, ChevronDown } from "lucide-react";
import { getApp, type AppId } from "../apps.config";
import { AppIcon } from "./AppIcon";
import { AppSwitcher } from "./AppSwitcher";

/**
 * RenewalTopBar — the unified shell. Paste ONE of these at the top of every
 * app's layout and the whole suite reads as one product.
 *
 *   <RenewalTopBar current="finance" user={{ initials: "JT" }} />
 *
 * - Left: RI color mark + wordmark (put ri-mark.png in /public — the COLOR emblem;
 *   never the white one, which is invisible on the ink bar).
 * - App-switcher button shows the CURRENT app's icon + name; click to jump.
 * - Optional global search, a bell slot, and the account avatar.
 * The bar color follows --ri-chrome (Ink by default); override that CSS
 * variable per-deploy if you ever want a different chrome.
 */
export interface RenewalTopBarProps {
  current: AppId;
  user?: { initials: string; name?: string };
  /** Path to the color mark in your /public folder (default /ri-mark.png). */
  emblemSrc?: string;
  showSearch?: boolean;
  searchPlaceholder?: string;
  onSearch?: (value: string) => void;
  /** Navigation hook for the switcher (e.g. Next router.push). */
  onNavigate?: (id: AppId, url: string) => void;
  /** Extra controls rendered just before the avatar (notifications, help…). */
  rightSlot?: React.ReactNode;
  onAvatarClick?: () => void;
}

export function RenewalTopBar({
  current,
  user,
  emblemSrc = "/ri-mark.png",
  showSearch = true,
  searchPlaceholder = "Search…",
  onSearch,
  onNavigate,
  rightSlot,
  onAvatarClick,
}: RenewalTopBarProps) {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const meta = getApp(current);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return (
    <header
      className="flex items-center gap-4 px-5 text-[var(--ri-cream)]"
      style={{ height: 60, background: "var(--ri-chrome)" }}
    >
      {/* Brand mark */}
      <a href="https://tools.renewalinitiatives.org" className="flex items-center gap-3 shrink-0">
        <img src={emblemSrc} alt="Renewal Initiatives" style={{ width: 34, height: 34, borderRadius: 8 }} />
        <span className="hidden sm:inline [font-family:var(--ri-font-display)] font-extrabold text-[20px] tracking-[0.01em] leading-none">
          Renewal Initiatives
        </span>
      </a>

      {/* App switcher */}
      <div ref={wrapRef} className="relative">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 rounded-[var(--ri-radius)] px-3 py-1.5 bg-white/10 hover:bg-white/15 transition-colors"
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <AppIcon app={current} tile={false} size={16} strokeWidth={1.9} className="!text-[var(--ri-harvest-gold)]" />
          <span className="font-semibold text-[15px]">{meta.name}</span>
          <ChevronDown size={15} className="opacity-70" />
        </button>
        <AppSwitcher current={current} open={open} onSelect={onNavigate} />
      </div>

      {/* Search */}
      {showSearch && (
        <label className="hidden md:flex items-center gap-2.5 rounded-[var(--ri-radius)] px-3 bg-black/15 h-9 min-w-[220px]">
          <Search size={15} className="opacity-60" />
          <input
            onChange={(e) => onSearch?.(e.target.value)}
            placeholder={searchPlaceholder}
            className="bg-transparent outline-none text-[14px] placeholder:text-[rgba(245,240,232,.55)] w-full"
          />
        </label>
      )}

      {/* Right cluster */}
      <div className="ml-auto flex items-center gap-3.5">
        {rightSlot ?? <Bell size={18} className="opacity-80" />}
        <button
          onClick={onAvatarClick}
          className="grid place-items-center rounded-full font-bold text-[12px] text-[var(--ri-forest-green)]"
          style={{ width: 30, height: 30, background: "var(--ri-harvest-gold)" }}
          title={user?.name ?? "Account"}
        >
          {user?.initials ?? "··"}
        </button>
      </div>
    </header>
  );
}
